import { useEffect, useState, useSyncExternalStore, type FormEvent } from 'react';
import {
  ArrowLeftIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  GearIcon,
  PersonIcon,
  PlusCircledIcon,
  ReloadIcon,
  LockClosedIcon,
  ReaderIcon,
} from '@radix-ui/react-icons';
import type {
  FoxKifuAiAccount,
  FoxKifuAiComputeMode,
  FoxKifuAiEngineWeight,
  FoxKifuAiExclusiveTier,
  FoxKifuAiUserAccess,
} from '@wx-weiqi/contracts';

import { presentAiAccessAction } from '../domain/account-access-presentation';
import { ZHIZI_EXCLUSIVE_TIER_LABELS, ZHIZI_EXCLUSIVE_TIERS } from '../domain/zhizi-profile';
import type { AdminStore } from '../stores/admin-store';
import { ZhiziWeightSelect } from './ZhiziWeightSelect';
import './teacher-ai-management.css';
import { accountLabel as displayAccount } from '../stores/ai-account-selection';

type AiManagementView = 'MENU' | 'ADD' | 'CONFIGURE' | 'ACCESS';

const NAVIGATION = [
  { view: 'MENU', label: '我的账号', icon: PersonIcon },
  { view: 'ADD', label: '添加账号', icon: PlusCircledIcon },
  { view: 'CONFIGURE', label: '配置参数', icon: GearIcon },
  { view: 'ACCESS', label: '管理权限', icon: ReaderIcon },
] as const;

function isSecureCredentialTransport(): boolean {
  const location = globalThis.location;
  return (
    location !== undefined &&
    (location.protocol === 'https:' ||
      location.hostname === 'localhost' ||
      location.hostname === '127.0.0.1' ||
      location.hostname === '::1')
  );
}

export function TeacherAiManagementPanel({
  admin,
  initialView = 'MENU',
}: {
  readonly admin: AdminStore;
  readonly initialView?: AiManagementView;
}) {
  const state = useSyncExternalStore(admin.subscribe, admin.snapshot, admin.snapshot);
  const accounts = state.aiManagement?.accounts ?? [];
  const firstAccount =
    accounts.find(
      (account) => account.configurationId === state.aiSelection?.defaultConfigurationId,
    ) ?? accounts[0];
  const [view, setView] = useState<AiManagementView>(initialView);
  const [selectedId, setSelectedId] = useState(firstAccount?.configurationId ?? '');
  const [accountValue, setAccountValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [computeMode, setComputeMode] = useState<FoxKifuAiComputeMode>(
    firstAccount?.profile.computeMode ?? 'VIP_SHARED',
  );
  const [exclusiveTier, setExclusiveTier] = useState<FoxKifuAiExclusiveTier>(
    firstAccount?.profile.exclusiveTier ?? 'ON_DEMAND_1X',
  );
  const [engineWeight, setEngineWeight] = useState<FoxKifuAiEngineWeight>(
    firstAccount?.profile.engineWeight ?? '10b512t',
  );
  const [maxVisits, setMaxVisits] = useState(firstAccount?.profile.maxVisits ?? 1_000);
  const [accountLabel, setAccountLabel] = useState(firstAccount?.accountLabel ?? '');
  const [notice, setNotice] = useState<string | null>(null);
  const selectedAccount = accounts.find((account) => account.configurationId === selectedId);
  const defaultAccount = accounts.find(
    (account) => account.configurationId === state.aiSelection?.defaultConfigurationId,
  );
  const accesses = selectedId === '' ? undefined : state.aiAccessByAccount[selectedId];
  const busy = state.pending !== null;
  const canSendCredentials = isSecureCredentialTransport();
  const loading = state.pending === 'zhizi-management-load';

  useEffect(() => {
    if (notice === null) return;
    const timer = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const applyAccountProfile = (account: FoxKifuAiAccount) => {
    setAccountLabel(account.accountLabel ?? '');
    setComputeMode(account.profile.computeMode);
    setExclusiveTier(account.profile.exclusiveTier ?? 'ON_DEMAND_1X');
    setEngineWeight(account.profile.engineWeight);
    setMaxVisits(account.profile.maxVisits);
  };

  useEffect(() => {
    if (selectedId !== '' && accounts.some((account) => account.configurationId === selectedId)) {
      return;
    }
    const first =
      accounts.find(
        (account) => account.configurationId === state.aiSelection?.defaultConfigurationId,
      ) ?? accounts[0];
    if (first === undefined) {
      setSelectedId('');
      return;
    }
    setSelectedId(first.configurationId);
    applyAccountProfile(first);
  }, [accounts, selectedId, state.aiSelection?.defaultConfigurationId]);

  const openView = (next: AiManagementView) => {
    if (next === view) return;
    setNotice(null);
    setView(next);
    setShowPassword(false);
    const first = selectedAccount ?? accounts[0];
    if (first !== undefined) {
      setSelectedId(first.configurationId);
      applyAccountProfile(first);
      if (next === 'ACCESS') void admin.loadAiAccess(first.configurationId).catch(() => undefined);
    }
  };

  const selectAccount = (configurationId: string, loadAccess: boolean) => {
    setNotice(null);
    setSelectedId(configurationId);
    const account = accounts.find((candidate) => candidate.configurationId === configurationId);
    if (account !== undefined) applyAccountProfile(account);
    if (loadAccess) void admin.loadAiAccess(configurationId).catch(() => undefined);
  };

  const submitAccount = (event: FormEvent) => {
    event.preventDefault();
    if (busy || !canSendCredentials) return;
    void admin
      .addZhiziAccount(accountValue, passwordValue)
      .then((created) => {
        setAccountValue('');
        setPasswordValue('');
        setShowPassword(false);
        setSelectedId(created.configurationId);
        applyAccountProfile(created);
        setNotice('智子云账号已验证并添加。');
        setView('CONFIGURE');
      })
      .catch(() => undefined);
  };

  const submitConfiguration = (event: FormEvent) => {
    event.preventDefault();
    if (busy || selectedAccount === undefined) return;
    void admin
      .configureZhiziAccount(
        selectedAccount,
        computeMode === 'VIP_SHARED'
          ? { mode: 'VIP_SHARED', weight: engineWeight, maxVisits }
          : {
              mode: 'EXCLUSIVE',
              tier: exclusiveTier,
              weight: engineWeight,
              maxVisits,
            },
        accountLabel,
      )
      .then(() => setNotice('算力参数已保存。'))
      .catch(() => undefined);
  };

  return (
    <div className="teacher-ai-management" aria-busy={busy}>
      <nav className="teacher-ai-management__nav" aria-label="AI管理功能">
        {NAVIGATION.map(({ view: item, label, icon: Icon }) => (
          <button
            key={item}
            type="button"
            aria-current={view === item ? 'page' : undefined}
            disabled={
              busy || ((item === 'CONFIGURE' || item === 'ACCESS') && accounts.length === 0)
            }
            onClick={() => openView(item)}
          >
            <Icon aria-hidden="true" />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="teacher-ai-management__content">
        {state.error === null ? null : (
          <div className="teacher-ai-management__alert" role="alert">
            <span>{state.error}</span>
            <button
              type="button"
              className="teacher-ai-management__button"
              disabled={busy}
              onClick={() =>
                void (
                  view === 'ACCESS' && selectedId
                    ? admin.loadAiAccess(selectedId)
                    : admin.loadAiManagement()
                ).catch(() => undefined)
              }
            >
              <ReloadIcon aria-hidden="true" />
              重新加载
            </button>
          </div>
        )}
        {notice === null ? null : (
          <p className="teacher-ai-management__notice" role="status">
            {notice}
          </p>
        )}

        {view === 'MENU' ? (
          <div className="teacher-ai-management__menu">
            <header className="teacher-ai-management__heading">
              <h3>我的账号</h3>
            </header>
            {loading ? (
              <p role="status" className="teacher-ai-management__empty">
                正在加载算力账号…
              </p>
            ) : state.aiManagement === null ? (
              <p className="teacher-ai-management__empty">暂未获取账号列表，请重新加载。</p>
            ) : (
              <>
                <section className="teacher-ai-management__default" aria-label="我的默认算力账号">
                  <div>
                    <strong>我的默认：</strong>
                    <span>
                      {accounts.length === 0
                        ? '尚未添加账号'
                        : defaultAccount !== undefined
                          ? displayAccount(defaultAccount)
                          : state.aiSelection?.defaultConfigurationId
                            ? '原默认账号已不可用：目前沿用棋院账号，请重新选择。'
                            : '尚未设置：目前沿用棋院账号，请明确选择。'}
                    </span>
                  </div>
                  <small>仅影响我，不改变学生授权。</small>
                </section>
                {accounts.length === 0 ? (
                  <div className="teacher-ai-management__empty">
                    <p>当前棋院还没有 AI 账号。</p>
                    <button
                      type="button"
                      className="teacher-ai-management__button is-primary"
                      disabled={busy}
                      onClick={() => openView('ADD')}
                    >
                      添加账号
                    </button>
                  </div>
                ) : (
                  <div className="teacher-ai-management__table-wrap">
                    <table className="teacher-ai-management__table" aria-label="棋院AI账号">
                      <thead>
                        <tr>
                          <th scope="col">账号名称</th>
                          <th scope="col">脱敏账号 / 编号</th>
                          <th scope="col">个人默认</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts.map((account) => (
                          <tr key={account.configurationId}>
                            <td data-label="账号名称">
                              <strong>{account.accountLabel || '未设置备注'}</strong>
                            </td>
                            <td data-label="脱敏账号 / 编号">
                              <span className="teacher-ai-management__identity">
                                {account.accountMasked}
                                <small>编号 {account.configurationId.slice(-6)}</small>
                              </span>
                            </td>
                            <td data-label="个人默认">
                              {account.configurationId ===
                              state.aiSelection?.defaultConfigurationId ? (
                                <span className="teacher-ai-management__badge is-positive">
                                  我的默认
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="teacher-ai-management__button"
                                  disabled={busy}
                                  aria-label={`将 ${displayAccount(account)} 设为我的默认账号`}
                                  onClick={() =>
                                    void admin
                                      .saveAiDefault(account.configurationId)
                                      .then(() =>
                                        setNotice(
                                          '已保存为我的默认算力账号，不影响其他教师和学生。',
                                        ),
                                      )
                                      .catch(() => undefined)
                                  }
                                >
                                  设为默认
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="teacher-ai-management__help">
                  共 {accounts.length}{' '}
                  个账号，可在“配置参数”中修改备注以便区分。凭据不会在页面回显。
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="teacher-ai-management__detail">
            <header className="teacher-ai-management__heading">
              <h3>
                {view === 'ADD' ? '添加账号' : view === 'CONFIGURE' ? '配置参数' : '管理权限'}
              </h3>
              <p>
                {view === 'ADD'
                  ? '验证并保存智子云账号。'
                  : view === 'CONFIGURE'
                    ? '设置算力模式、档位与分析参数。'
                    : '管理当前棋院学生的使用权限。'}
              </p>
            </header>

            {view === 'ADD' ? (
              <form
                className="teacher-ai-management__form teacher-ai-management__form--add"
                onSubmit={submitAccount}
              >
                <label>
                  智子云账号
                  <input
                    value={accountValue}
                    onChange={(event) => setAccountValue(event.target.value)}
                    placeholder="手机号或邮箱"
                    minLength={3}
                    maxLength={320}
                    autoComplete="off"
                    required
                    disabled={busy}
                  />
                </label>
                <label>
                  智子云密码
                  <span className="teacher-ai-management__password">
                    <input
                      aria-label="智子云密码"
                      type={showPassword ? 'text' : 'password'}
                      value={passwordValue}
                      onChange={(event) => setPasswordValue(event.target.value)}
                      maxLength={8192}
                      autoComplete="new-password"
                      required
                      disabled={busy}
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? '隐藏密码' : '显示密码'}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOpenIcon aria-hidden="true" />
                      ) : (
                        <EyeClosedIcon aria-hidden="true" />
                      )}
                    </button>
                  </span>
                </label>
                <p className="teacher-ai-management__help teacher-ai-management__security">
                  <LockClosedIcon aria-hidden="true" />
                  {canSendCredentials
                    ? '系统会验证账号有效性并加密保存凭据。'
                    : '当前连接不是 HTTPS，请通过 HTTPS 或服务器本机回环地址配置。'}
                </p>
                <footer className="teacher-ai-management__footer">
                  <button
                    className="teacher-ai-management__button return-button"
                    type="button"
                    disabled={busy}
                    onClick={() => openView('MENU')}
                  >
                    <ArrowLeftIcon aria-hidden="true" />
                    返回
                  </button>
                  <button
                    className="teacher-ai-management__button is-primary"
                    type="submit"
                    disabled={busy || !canSendCredentials}
                  >
                    {state.pending === 'zhizi-account-add' ? '正在验证…' : '验证并添加'}
                  </button>
                </footer>
              </form>
            ) : null}

            {view === 'CONFIGURE' ? (
              <form className="teacher-ai-management__form" onSubmit={submitConfiguration}>
                <AccountSelect
                  accounts={accounts}
                  value={selectedId}
                  disabled={busy}
                  onChange={(id) => selectAccount(id, false)}
                />
                <fieldset className="teacher-ai-management__fieldset">
                  <legend>账号信息</legend>
                  <label>
                    账号备注
                    <input
                      value={accountLabel}
                      maxLength={60}
                      disabled={busy}
                      placeholder="例如：课堂教学、课后复盘"
                      onChange={(event) => setAccountLabel(event.target.value)}
                    />
                  </label>
                </fieldset>
                <fieldset className="teacher-ai-management__fieldset">
                  <legend>算力设置</legend>
                  <div className="teacher-ai-management__form-grid">
                    <label>
                      算力模式
                      <select
                        value={computeMode}
                        disabled={busy}
                        onChange={(event) =>
                          setComputeMode(event.target.value as FoxKifuAiComputeMode)
                        }
                      >
                        <option value="VIP_SHARED">智子 VIP 共享</option>
                        <option value="EXCLUSIVE">智子独占算力</option>
                      </select>
                    </label>
                    {computeMode === 'EXCLUSIVE' ? (
                      <label>
                        档位
                        <select
                          value={exclusiveTier}
                          disabled={busy}
                          onChange={(event) =>
                            setExclusiveTier(event.target.value as FoxKifuAiExclusiveTier)
                          }
                        >
                          {ZHIZI_EXCLUSIVE_TIERS.map((tier) => (
                            <option key={tier} value={tier}>
                              {ZHIZI_EXCLUSIVE_TIER_LABELS[tier]}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    <ZhiziWeightSelect
                      key={selectedId}
                      admin={admin}
                      accountId={selectedId}
                      value={engineWeight}
                      disabled={busy}
                      onChange={setEngineWeight}
                    />
                    <label>
                      分析参数（visits）
                      <input
                        type="number"
                        min={0}
                        max={100000}
                        step={100}
                        value={maxVisits}
                        disabled={busy}
                        onChange={(event) => setMaxVisits(Number(event.target.value))}
                      />
                      <small>0 表示不封顶；其他值至少为 100。</small>
                    </label>
                  </div>
                </fieldset>
                <footer className="teacher-ai-management__footer">
                  <button
                    className="teacher-ai-management__button return-button"
                    type="button"
                    disabled={busy}
                    onClick={() => openView('MENU')}
                  >
                    <ArrowLeftIcon aria-hidden="true" />
                    返回
                  </button>
                  <button
                    className="teacher-ai-management__button is-primary"
                    type="submit"
                    disabled={
                      busy || selectedAccount === undefined || (maxVisits !== 0 && maxVisits < 100)
                    }
                  >
                    {state.pending === `zhizi-configuration-${selectedId}`
                      ? '正在保存…'
                      : '保存参数'}
                  </button>
                </footer>
              </form>
            ) : null}

            {view === 'ACCESS' ? (
              <div className="teacher-ai-management__access">
                <AccountSelect
                  accounts={accounts}
                  value={selectedId}
                  disabled={busy}
                  onChange={(id) => selectAccount(id, true)}
                />
                <div className="teacher-ai-management__table-wrap">
                  {accesses === undefined ? (
                    <p className="teacher-ai-management__empty" role="status">
                      {state.error ? '权限列表未加载，请重试。' : '正在加载学生账号…'}
                    </p>
                  ) : accesses.length === 0 ? (
                    <p className="teacher-ai-management__empty">当前棋院暂无学生账号。</p>
                  ) : (
                    <AiAccessTable
                      accesses={accesses}
                      busy={busy}
                      onUpdate={(access) =>
                        void admin
                          .updateAiAccess(selectedId, access, !access.authorized)
                          .catch(() => undefined)
                      }
                    />
                  )}
                </div>
                <p className="teacher-ai-management__help">
                  授权操作即时生效，仅影响所选学生；不会更改教师的默认账号。
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountSelect({
  accounts,
  value,
  disabled,
  onChange,
}: {
  readonly accounts: readonly FoxKifuAiAccount[];
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (configurationId: string) => void;
}) {
  return (
    <label className="teacher-ai-management__account-select">
      当前账号
      <select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        {accounts.map((account) => (
          <option key={account.configurationId} value={account.configurationId}>
            {displayAccount(account)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AiAccessTable({
  accesses,
  busy,
  onUpdate,
}: {
  readonly accesses: readonly FoxKifuAiUserAccess[];
  readonly busy: boolean;
  readonly onUpdate: (access: FoxKifuAiUserAccess) => void;
}) {
  return (
    <table className="teacher-ai-management__table" aria-label="学生AI使用权限">
      <thead>
        <tr>
          {['学生', '账号状态', '授权', '操作'].map((label) => (
            <th key={label} scope="col">
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {accesses.map((access) => {
          const action = presentAiAccessAction(access);
          const active = access.membershipStatus === 'ACTIVE' && access.userStatus === 'ACTIVE';
          return (
            <tr key={access.userId}>
              <td data-label="学生">
                <span className="teacher-ai-management__identity">
                  <strong>{access.displayName}</strong>
                  <small>{access.accountUsername ?? '微信账号'}</small>
                </span>
              </td>
              <td data-label="账号状态">
                <span
                  className={`teacher-ai-management__badge ${active ? 'is-positive' : 'is-negative'}`}
                >
                  {active ? '正常' : '已停用'}
                </span>
              </td>
              <td data-label="授权">
                <span
                  className={`teacher-ai-management__badge ${access.authorized ? 'is-positive' : ''}`}
                >
                  {access.authorized ? '已授权' : '未授权'}
                </span>
              </td>
              <td data-label="操作">
                <button
                  type="button"
                  className={`teacher-ai-management__button ${access.authorized ? 'is-danger' : ''}`}
                  disabled={busy || action.disabled}
                  onClick={() => onUpdate(access)}
                  aria-label={`${action.label}：${access.displayName}（${access.accountUsername ?? '微信账号'}）`}
                >
                  {access.authorized ? '取消权限' : action.disabled ? '需恢复登录' : '授予权限'}
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
